import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import { useNotification } from "../context/NotificationContext";
import "../styles/Upload.css";

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Doğrulama sabitleri — sunucu tarafındaki (uploadRoutes.js) kurallarla birebir
// aynı tutulmalıdır. Tek kaynak olarak bu dosyadan yönetin.
// ---------------------------------------------------------------------------
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILENAME_LENGTH = 200;
const ALLOWED_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".ppt", ".pptx",
  ".xls", ".xlsx", ".zip", ".rar", ".txt",
  ".jpg", ".jpeg", ".png", ".gif",
  ".mp4", ".mp3", ".avi",
];

const CATEGORY_TONE = { Ders: "ders", Proje: "proje", "Sınav": "sinav", Genel: "genel" };

const ICONS = {
  upload: (<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>),
  check: <polyline points="20 6 9 17 4 12" />,
  search: (<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  file: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>),
  folder: <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />,
  close: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
};

const Icon = ({ name, className = "" }) => (
  <svg className={`up-icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

// --------------------------------------------------------------------------
// Yardımcılar
// --------------------------------------------------------------------------

/** Dosya adından küçük harfli uzantıyı çıkarır (".pdf" gibi, nokta dahil). */
const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex === -1 ? "" : fileName.slice(dotIndex).toLowerCase();
};

const getFileExt = (fileName) =>
  fileName && fileName.includes(".") ? fileName.split(".").pop().slice(0, 4).toUpperCase() : "DOSYA";

const safeDecode = (text) => {
  try {
    return decodeURIComponent(text || "");
  } catch (e) {
    return text || "";
  }
};

const lower = (text) => (text || "").toLocaleLowerCase("tr-TR");

/** Bayt değerini okunabilir boyuta çevirir (negatif/gövdesiz değerler için güvenli). */
const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const formatResourceDate = (value) => {
  const d = value ? new Date(value) : null;
  return d && !isNaN(d) ? d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }) : "";
};

/**
 * Tek bir dosyayı istemci tarafı kurallara göre denetler.
 * Hata varsa kullanıcıya gösterilecek Türkçe mesajı, yoksa null döner.
 */
const validateFile = (file) => {
  if (!file) return "Geçersiz dosya.";

  if (!file.name || typeof file.name !== "string") {
    return "Dosya adı okunamadı, dosya seçimi atlandı.";
  }
  if (file.name.length > MAX_FILENAME_LENGTH) {
    return `"${file.name.slice(0, 30)}..." adı çok uzun (en fazla ${MAX_FILENAME_LENGTH} karakter).`;
  }
  const ext = getFileExtension(file.name);
  if (!ext) {
    return `"${file.name}" uzantısız; uzantılı dosyalar yükleyebilirsiniz.`;
  }
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `"${file.name}" desteklenmiyor. İzin verilen türler: ${ALLOWED_EXTENSIONS.join(", ")}`;
  }
  if (!Number.isFinite(file.size) || file.size === 0) {
    return `"${file.name}" boş görünüyor (0 bayt) veya boyutu okunamadı.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `"${file.name}" boyutu ${formatFileSize(file.size)}; maksimum ${formatFileSize(MAX_FILE_SIZE)} olabilir.`;
  }
  return null;
};

/** JWT token'ı çözer, imza/exp kontrolü yapmadan süresi dolmuşsa hata fırlatır. */
const parseTokenPayload = (token) => {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Token biçimi geçersiz.");
  const payload = JSON.parse(atob(parts[1]));
  if (payload.exp && payload.exp * 1000 < Date.now()) {
    throw new Error("Oturum süresi dolmuş.");
  }
  return payload;
};

/* ---------- Onay penceresi (mobilde alttan açılan panel) ---------- */
const ConfirmDialog = ({ title, message, confirmLabel, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onCancelRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    await onConfirm();
  };

  return (
    <div className="up-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="up-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="up-modal-head">
          <h3>{title}</h3>
          <button type="button" className="up-modal-close" onClick={onCancel} disabled={busy} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </div>
        <div className="up-modal-body">
          <p>{message}</p>
          <div className="up-modal-actions">
            <button type="button" className="up-btn up-btn-outline" onClick={onCancel} disabled={busy}>Vazgeç</button>
            <button type="button" className="up-btn up-btn-danger-solid" onClick={handleConfirm} disabled={busy} autoFocus>
              {busy ? "Siliniyor..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Upload() {
  const [groups, setGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupInfo, setSelectedGroupInfo] = useState(null);
  const [resources, setResources] = useState([]);
  const [resourceQuery, setResourceQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // 'idle', 'uploading', 'success', 'error'
  const [uploadError, setUploadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmResource, setConfirmResource] = useState(null);

  const { showNotification } = useNotification();
  const notifyRef = useRef(showNotification);
  notifyRef.current = showNotification;

  const socketRef = useRef(null);
  const isUploadingRef = useRef(false); // çift gönderim koruması
  const abortRef = useRef(null);
  const mainRef = useRef(null);
  const statusTimer = useRef(null);
  const dragDepth = useRef(0);
  const navigate = useNavigate();

  const myId = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      const payload = token ? JSON.parse(atob(token.split(".")[1])) : null;
      return payload ? payload.userId ?? payload.id ?? null : null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => () => {
    clearTimeout(statusTimer.current);
    abortRef.current?.abort();
  }, []);

  // Oturum kontrolü
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      showNotification("Oturum bulunamadı. Lütfen giriş yapınız.", "error");
      navigate("/login");
      setLoading(false);
      return;
    }

    try {
      parseTokenPayload(token);
    } catch (e) {
      console.error("Token çözümleme hatası:", e.message);
      localStorage.removeItem("token"); // süresi dolmuş/geçersiz token'ı temizle
      showNotification(
        e.message === "Oturum süresi dolmuş."
          ? "Oturum süreniz doldu. Lütfen tekrar giriş yapınız."
          : "Geçersiz token. Lütfen tekrar giriş yapınız.",
        "error"
      );
      navigate("/login");
      setLoading(false);
    }
  }, [navigate, showNotification]);

  // Socket.IO bağlantısı: bildirim fonksiyonu değişse bile yeniden bağlanmaz
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const socket = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("request_user_data");
    });

    socket.on("user_groups", (groupsData) => {
      setGroups(Array.isArray(groupsData) ? groupsData : []);
      setLoading(false);
    });

    socket.on("connect_error", (err) => {
      console.error("Socket.IO Bağlantı Hatası:", err.message);
      notifyRef.current("Bağlantı hatası oluştu.", "error");
      setLoading(false);
    });

    socket.on("error", (data) => {
      console.error("Socket Hatası (Sunucudan):", data.message);
      notifyRef.current(data.message, "error");
    });

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, []);

  const filteredGroups = useMemo(() => {
    const query = lower(searchQuery.trim());
    if (!query) return groups;
    return groups.filter(
      (g) => lower(g.name).includes(query) || lower(g.description).includes(query) || lower(g.school).includes(query)
    );
  }, [groups, searchQuery]);

  const fetchResources = useCallback(
    async (groupId) => {
      // Grup kimliği sunucuya gitmeden önce doğrulanır (URL enjeksiyonuna karşı)
      const parsedGroupId = Number(groupId);
      if (!Number.isInteger(parsedGroupId) || parsedGroupId <= 0) return;

      const token = localStorage.getItem("token");
      if (!token) {
        showNotification("Oturum bulunamadı. Lütfen giriş yapınız.", "error");
        return;
      }

      setResourcesLoading(true);

      try {
        const response = await axios.get(`${API_URL}/api/groups/${parsedGroupId}/files`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.data.success || !Array.isArray(response.data.files)) {
          throw new Error("Sunucudan geçersiz yanıt: files dizisi eksik veya hatalı.");
        }
        setResources(response.data.files);
      } catch (error) {
        console.error("Kaynaklar yüklenirken hata:", error.response ? error.response.data : error.message);
        if (error.response?.status === 401) {
          showNotification("Oturumunuz geçersiz. Lütfen tekrar giriş yapınız.", "error");
          navigate("/login");
        } else if (error.response?.status === 403) {
          showNotification("Bu grubun kaynaklarına erişim yetkiniz yok.", "error");
          setSelectedGroup(null);
          setSelectedGroupInfo(null);
        } else if (error.response?.status === 404) {
          setResources([]);
        } else {
          showNotification(
            `Kaynaklar yüklenirken hata: ${error.response?.data?.message || error.message}`,
            "error"
          );
          setResources([]);
        }
      } finally {
        setResourcesLoading(false);
      }
    },
    [showNotification, navigate]
  );

  useEffect(() => {
    if (selectedGroup) {
      const groupInfo = groups.find((g) => g.id === selectedGroup);
      // Seçilen grup artık üyesi olunan gruplar arasında değilse seçimi sıfırla
      if (!groupInfo) {
        setSelectedGroup(null);
        setSelectedGroupInfo(null);
        setResources([]);
        return;
      }
      setSelectedGroupInfo(groupInfo);
      fetchResources(selectedGroup);
    } else {
      setSelectedGroupInfo(null);
      setResources([]);
    }
  }, [selectedGroup, groups, fetchResources]);

  // Canlı mesaj/dosya akışı
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    const onGroupMessage = (msg) => {
      // Socket olayları camelCase (groupId) kullanır; HTTP yükünde her iki biçim de olabilir
      const msgGroupId = Number(msg?.groupId ?? msg?.group_id);
      if (msg && msgGroupId === Number(selectedGroup)) fetchResources(selectedGroup);
    };
    // Başka bir kullanıcının sildiği dosya listede anında kaybolsun
    const onFileDeleted = (data) => {
      if (data && Number(data.groupId) === Number(selectedGroup)) fetchResources(selectedGroup);
    };

    socket.on("groupMessage", onGroupMessage);
    socket.on("file_deleted", onFileDeleted);
    return () => {
      socket.off("groupMessage", onGroupMessage);
      socket.off("file_deleted", onFileDeleted);
    };
  }, [selectedGroup, fetchResources]);

  const resetUploadState = () => {
    clearTimeout(statusTimer.current);
    setSelectedFile(null);
    setUploadStatus("idle");
    setUploadError("");
    setUploadProgress(0);
  };

  const handleGroupSelect = (groupId) => {
    if (groupId === selectedGroup) return;
    setSelectedGroup(groupId);
    setResourceQuery("");
    resetUploadState();
    // Telefonda liste üstte kalır: seçimden sonra içeriğe kaydır
    if (window.innerWidth < 900) {
      setTimeout(() => mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  };

  // Dosya seçme ve sürükle-bırak aynı yoldan geçer; sunucu tek dosya kabul eder
  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      showNotification("Tek seferde bir dosya yüklenebilir; ilk dosya seçildi.", "error");
    }
    const file = files[0];
    const error = validateFile(file);
    if (error) {
      showNotification(error, "error");
      return;
    }
    clearTimeout(statusTimer.current);
    setSelectedFile(file);
    setUploadStatus("idle");
    setUploadError("");
    setUploadProgress(0);
  };

  const handleFileChange = (e) => {
    handleFiles(e.target.files);
    // Aynı dosyanın yeniden seçilebilmesi için input değerini sıfırla
    e.target.value = "";
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (uploadStatus === "uploading") return;
    handleFiles(e.dataTransfer?.files);
  };

  const cancelUpload = () => abortRef.current?.abort();

  const handleUpload = async () => {
    // Çift gönderim koruması
    if (isUploadingRef.current || uploadStatus === "uploading") return;

    if (!selectedGroup) {
      showNotification("Lütfen bir grup seçin.", "error");
      return;
    }
    if (!selectedFile) {
      showNotification("Lütfen yüklenecek bir dosya seçin.", "error");
      return;
    }

    // Dosyayı gönderim öncesi son kez denetle (silinme/değişme ihtimaline karşı)
    const preError = validateFile(selectedFile);
    if (preError) {
      setUploadStatus("error");
      setUploadError(preError);
      showNotification(preError, "error");
      setSelectedFile(null);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      showNotification("Oturum bulunamadı. Lütfen giriş yapınız.", "error");
      navigate("/login");
      return;
    }

    isUploadingRef.current = true;
    setUploadStatus("uploading");
    setUploadError("");
    setUploadProgress(0);

    const controller = new AbortController();
    abortRef.current = controller;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("groupId", selectedGroup);

    try {
      await axios.post(`${API_URL}/api/upload-file`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total ?? selectedFile.size;
          const percentCompleted = total > 0 ? Math.min(100, Math.round((progressEvent.loaded * 100) / total)) : 0;
          setUploadProgress(percentCompleted);
        },
      });

      setUploadStatus("success");
      showNotification("Dosya başarıyla yüklendi!", "success");
      setSelectedFile(null);
      statusTimer.current = setTimeout(() => setUploadStatus("idle"), 4000);
      await fetchResources(selectedGroup);
    } catch (error) {
      if (axios.isCancel(error)) {
        setUploadStatus("idle");
        setUploadProgress(0);
        showNotification("Yükleme iptal edildi.", "error");
        return;
      }
      console.error("Dosya yükleme hatası:", error.response ? error.response.data : error.message);
      let message = "Dosya yüklenirken sunucu hatası oluştu.";
      if (error.response?.status === 401) {
        message = "Oturumunuz geçersiz. Lütfen tekrar giriş yapınız.";
        navigate("/login");
      } else if (error.response?.status === 403) {
        message = "Bu gruba dosya yükleme yetkiniz yok.";
      } else if (error.response?.status === 413) {
        message = "Dosya boyutu çok büyük. Maksimum 10MB dosya yükleyebilirsiniz.";
      } else if (error.response?.status === 400) {
        message = `Dosya yükleme başarısız: ${error.response.data?.message || "Geçersiz istek."}`;
      }
      setUploadStatus("error");
      setUploadError(message);
      showNotification(message, "error");
    } finally {
      isUploadingRef.current = false;
      abortRef.current = null;
    }
  };

  const handleDelete = async (messageId) => {
    // Kimlik gönderilmeden önce doğrulanır (URL enjeksiyonuna karşı)
    const parsedId = Number(messageId);
    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      showNotification("Geçersiz dosya kaydı.", "error");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      showNotification("Oturum bulunamadı. Lütfen giriş yapınız.", "error");
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/delete-file/${parsedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showNotification("Dosya başarıyla silindi.", "success");
      await fetchResources(selectedGroup);
    } catch (error) {
      console.error("Dosya silme hatası:", error.response ? error.response.data : error.message);
      if (error.response?.status === 401) {
        showNotification("Oturumunuz geçersiz. Lütfen tekrar giriş yapınız.", "error");
        navigate("/login");
      } else if (error.response?.status === 403) {
        showNotification("Bu dosyayı silme yetkiniz yok.", "error");
      } else if (error.response?.status === 404) {
        showNotification("Dosya bulunamadı.", "error");
        await fetchResources(selectedGroup);
      } else {
        showNotification("Dosya silinirken sunucu hatası oluştu.", "error");
      }
    }
  };

  // Kaynaklar: en yeni üstte, arama ile süzülür
  const visibleResources = useMemo(() => {
    const query = lower(resourceQuery.trim());
    return [...resources]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .filter((r) => !query || lower(safeDecode(r.fileName)).includes(query) || lower(r.senderName).includes(query));
  }, [resources, resourceQuery]);

  // Sunucu yine de yetkiyi denetler; bilinen başka birinin dosyasında düğme gösterilmez
  const canDelete = (resource) => {
    const ownerId = resource.senderId ?? resource.sender_id ?? resource.userId ?? resource.user_id;
    return ownerId === undefined || ownerId === null || myId === null || String(ownerId) === String(myId);
  };

  const isUploading = uploadStatus === "uploading";

  return (
    <div className="up-page">
      {/* Başlık */}
      <header className="up-head">
        <div className="up-container">
          <h1>Notlarınız, <span className="up-mark">tek yerde.</span></h1>
          <p>Ders notları, ödevler ve kaynakları kolayca yükleyin ve güvenle paylaşın.</p>
        </div>
      </header>

      <main className="up-container up-layout">
        {/* Sol: gruplar */}
        <section className="up-groups" aria-labelledby="up-groups-title">
          <h2 id="up-groups-title" className="up-section-title">
            Üye olduğum gruplar <span>{filteredGroups.length}</span>
          </h2>

          <label className="up-search">
            <Icon name="search" className="up-search-icon" />
            <input
              type="search"
              placeholder="Grup adı, açıklama veya okul ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="up-search-input"
              aria-label="Grup ara"
            />
          </label>

          <div className="up-groups-list">
            {loading ? (
              [0, 1, 2].map((i) => <span className="up-group up-skeleton" key={i} aria-hidden="true"><i /><i /><i /></span>)
            ) : filteredGroups.length > 0 ? (
              filteredGroups.map((group) => (
                <button
                  type="button"
                  key={group.id}
                  className={`up-group${selectedGroup === group.id ? " is-selected" : ""}`}
                  onClick={() => handleGroupSelect(group.id)}
                  aria-pressed={selectedGroup === group.id}
                >
                  <span className="up-group-top">
                    <strong>{group.name}</strong>
                    {selectedGroup === group.id && (
                      <span className="up-group-check"><Icon name="check" /></span>
                    )}
                  </span>
                  <span className="up-tag" data-tone={CATEGORY_TONE[group.category || "Genel"] || "genel"}>
                    {group.category || "Genel"}
                  </span>
                  {group.description && <span className="up-group-desc">{group.description}</span>}
                  {Number(group.member_count) > 0 && <small>{group.member_count} üye</small>}
                </button>
              ))
            ) : (
              <div className="up-state">
                <p>
                  {searchQuery
                    ? `"${searchQuery}" ile eşleşen grup bulunamadı.`
                    : "Henüz herhangi bir gruba üye değilsiniz."}
                </p>
                <p>
                  Grup aramaya <Link to="/groups">buradan</Link> ulaşabilirsiniz.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Sağ: yükleme ve kaynaklar */}
        <div className="up-main" ref={mainRef}>
          {selectedGroup ? (
            <>
              {selectedGroupInfo && (
                <div className="up-selected">
                  <h2>{selectedGroupInfo.name}</h2>
                  <span className="up-tag" data-tone={CATEGORY_TONE[selectedGroupInfo.category || "Genel"] || "genel"}>
                    {selectedGroupInfo.category || "Genel"}
                  </span>
                </div>
              )}

              <section className="up-panel">
                <h3 className="up-section-title">Dosya paylaş</h3>

                <label
                  htmlFor="file-upload"
                  className={`up-drop${dragging ? " is-dragging" : ""}${isUploading ? " is-disabled" : ""}`}
                  tabIndex={0}
                  onDragEnter={onDragEnter}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isUploading) {
                      e.preventDefault();
                      document.getElementById("file-upload")?.click();
                    }
                  }}
                >
                  <span className="up-drop-icon"><Icon name="upload" /></span>
                  <span className="up-drop-title">{dragging ? "Dosyayı bırakın" : "Dosya seçin ya da buraya sürükleyin"}</span>
                  <span className="up-drop-hint">
                    PDF, DOC, PPT, XLS, ZIP, ses/video ve resim — en fazla {formatFileSize(MAX_FILE_SIZE)}, tek dosya.
                  </span>
                </label>
                <input
                  id="file-upload"
                  className="up-file-input"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  accept={ALLOWED_EXTENSIONS.join(",")}
                />

                {selectedFile && (
                  <div className="up-selected-files">
                    <div className="up-file-row">
                      <span className="up-ext">{getFileExt(selectedFile.name)}</span>
                      <span className="up-file-text">
                        <strong>{safeDecode(selectedFile.name)}</strong>
                        <small>{formatFileSize(selectedFile.size)}</small>
                      </span>
                      {!isUploading && (
                        <button type="button" className="up-icon-btn" onClick={resetUploadState} aria-label="Seçili dosyayı kaldır">
                          <Icon name="close" />
                        </button>
                      )}
                    </div>

                    {isUploading ? (
                      <div className="up-progress">
                        <div className="up-progress-track" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
                          <div className="up-progress-bar" style={{ width: `${uploadProgress}%` }} />
                        </div>
                        <div className="up-progress-row">
                          <small>%{uploadProgress} — yükleniyor, lütfen bekleyin...</small>
                          <button type="button" className="up-link-btn" onClick={cancelUpload}>İptal et</button>
                        </div>
                      </div>
                    ) : (
                      <div className="up-actions">
                        <button type="button" className="up-btn up-btn-outline" onClick={resetUploadState}>Temizle</button>
                        <button type="button" className="up-btn up-btn-solid" onClick={handleUpload}>Yükle</button>
                      </div>
                    )}
                  </div>
                )}

                <div aria-live="polite">
                  {uploadStatus === "success" && <div className="up-message is-success">Dosya başarıyla yüklendi!</div>}
                  {uploadStatus === "error" && <div className="up-message is-error">{uploadError || "Dosya yüklenirken hata oluştu."}</div>}
                </div>
              </section>

              <section className="up-panel">
                <h3 className="up-section-title">Grup kaynakları <span>{resources.length}</span></h3>

                {resources.length > 3 && (
                  <label className="up-search up-search-inline">
                    <Icon name="search" className="up-search-icon" />
                    <input
                      type="search"
                      placeholder="Dosya adı veya paylaşan ara..."
                      value={resourceQuery}
                      onChange={(e) => setResourceQuery(e.target.value)}
                      className="up-search-input"
                      aria-label="Kaynaklarda ara"
                    />
                  </label>
                )}

                {resourcesLoading && resources.length === 0 ? (
                  <ul className="up-resources" aria-hidden="true">
                    {[0, 1].map((i) => <li className="up-resource up-skeleton" key={i}><i /><i /><i /></li>)}
                  </ul>
                ) : visibleResources.length > 0 ? (
                  <ul className="up-resources">
                    {visibleResources.map((resource) => (
                      <li key={resource.id} className="up-resource">
                        <div className="up-resource-top">
                          <span className="up-ext">{getFileExt(resource.fileName)}</span>
                          <div className="up-resource-info">
                            <h4>{safeDecode(resource.fileName)}</h4>
                            <p>
                              <span>Paylaşan: {resource.senderName}</span>
                              <span>{formatResourceDate(resource.createdAt)}</span>
                            </p>
                          </div>
                        </div>
                        <div className="up-resource-actions">
                          <a
                            href={resource.fileUrl ? `${API_URL}${resource.fileUrl}` : undefined}
                            className="up-btn up-btn-outline"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              if (!resource.fileUrl) {
                                e.preventDefault();
                                showNotification("Bu dosya için indirme bağlantısı bulunamadı.", "error");
                              }
                            }}
                          >
                            Görüntüle / İndir
                          </a>
                          {canDelete(resource) && (
                            <button type="button" className="up-btn up-btn-danger" onClick={() => setConfirmResource(resource)}>
                              Sil
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : resources.length > 0 ? (
                  <div className="up-state">
                    <p>“{resourceQuery}” ile eşleşen kaynak yok.</p>
                  </div>
                ) : (
                  <div className="up-state">
                    <p>Bu grupta henüz kaynak paylaşılmamış.</p>
                    <p>İlk paylaşan siz olun!</p>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="up-empty">
              <span className="up-empty-icon"><Icon name="folder" /></span>
              <h2>Bir grup seçin</h2>
              <p>Kaynakları görüntülemek ve dosya paylaşmak için önce bir grup seçin.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="up-container up-foot">
        <Link to="/" className="up-back">← Ana sayfaya dön</Link>
      </footer>

      {confirmResource && (
        <ConfirmDialog
          title="Dosyayı sil"
          message={`“${safeDecode(confirmResource.fileName)}” dosyası silinsin mi? Bu işlem geri alınamaz.`}
          confirmLabel="Sil"
          onCancel={() => setConfirmResource(null)}
          onConfirm={async () => {
            try {
              await handleDelete(confirmResource.id);
            } finally {
              setConfirmResource(null);
            }
          }}
        />
      )}
    </div>
  );
}

export default Upload;